import { EventSystem, IEventSystem } from "@dhx/ts-common/events";
import { Id } from "@dhx/ts-common/types";
import { Loader } from "./datacollection/loader";
import { Sort } from "./datacollection/sort";
import { DataProxy } from "./dataproxy";
import {
	dhxError,
	dhxWarning,
	findByConf,
	isDebug,
	isEqualObj,
	copyWithoutInner,
	toDataDriver,
	isTreeCollection,
} from "./helpers";
import {
	DataCallback,
	DataEvents,
	IDataChangeStack,
	IDataCollection,
	IDataItem,
	IDataProxy,
	IFilterCallback,
	IFilterConfig,
	IFilterMode,
	ISortMode,
	ITreeCollection,
	IUpdateObject,
	ReduceCallBack,
	Statuses,
	IDataEventsHandlersMap,
	DataDriver,
	IDataConfig,
	ISortConfig,
	IDataDriver,
} from "./types";

import { copy, extend, findIndex, isNumeric, uid } from "@dhx/ts-common/core";
import { TreeCollection } from "./treecollection";

export class DataCollection<T extends IDataItem = IDataItem> implements IDataCollection<T> {
	public loadData: Promise<any>;
	public saveData: Promise<any>;
	public dataProxy: IDataProxy;
	public config: IDataConfig; // TODO: not final
	public events: IEventSystem<DataEvents, IDataEventsHandlersMap>;

	protected _order: T[];
	protected _pull: { [id: string]: T };
	protected _sort: Sort;
	protected _filter: any;
	protected _sorter: ISortMode;
	protected _meta: any;
	protected _range: [number, number];
	protected _loaded: boolean;
	protected _initOrder: T[];

	private _changes: IDataChangeStack = { order: [] };
	private _loader: Loader;

	constructor(config?: any, events?: IEventSystem<any>) {
		this.config = config || {};

		this._sort = new Sort();
		this._loader = new Loader(this, this._changes);
		this.events = events || new EventSystem<any>(this);

		this.events.on(DataEvents.dataRequest, (from, to) => {
			const proxy = this.dataProxy;
			if (proxy && proxy.updateUrl) {
				proxy.updateUrl(null, { from, limit: proxy.config.limit || to - from });
				this.load(proxy);
			}
		});

		this.events.on(DataEvents.loadError, response => {
			setTimeout(() => {
				if (typeof response !== "string") {
					dhxError(response);
				} else {
					dhxWarning(response);
				}
			}, 0);
		});

		this._reset();
	}

	protected _reset() {
		this._order = [];
		this._pull = {};
		this._changes = { order: [] };
		this._initOrder = null;
		this._meta = new WeakMap();
		this._loaded = false;
	}

	add(newItem: IDataItem, index?: number): Id;
	add(newItem: IDataItem[], index?: number): Id[];
	add(newItem: IDataItem | IDataItem[], index?: number): Id | Id[] {
		if (!this.events.fire(DataEvents.beforeAdd, [newItem])) {
			return;
		}
		let out: Id | Id[];
		if (Array.isArray(newItem)) {
			out = newItem.map((element, key) => {
				if (key !== 0) {
					index = index + 1;
				}
				return this._add(element, index);
			});
		} else {
			out = this._add(newItem, index);
		}

		this._applySmart();
		return out;
	}
	remove(id: Id | Id[]): void {
		if (id) {
			if (id instanceof Array) {
				[...id].map(elementId => {
					this._remove(elementId);
				});
			} else {
				this._remove(id);
			}
		}
	}
	removeAll(): void {
		this._reset();
		this.events.fire(DataEvents.removeAll);
		this.events.fire(DataEvents.change);
	}
	exists(id: Id): boolean {
		return !!this._pull[id];
	}
	getNearId(id: Id) {
		const item = this._pull[id];
		if (!item) {
			return this._order[0].id || "";
		}
	}
	getItem(id: Id): T {
		return this._pull[id];
	}
	update(id: Id, newItem: IUpdateObject, silent?: boolean) {
		const item = this.getItem(id);
		if (item) {
			if (isEqualObj(newItem, item)) {
				return;
			}

			if (newItem.id && id !== newItem.id) {
				dhxWarning("this method doesn't allow change id");
				if (isDebug()) {
					// eslint-disable-next-line no-debugger
					debugger;
				}
			} else {
				if (newItem.parent && item.parent && newItem.parent !== item.parent) {
					this.move(id, -1, this, newItem.parent);
				}
				extend(this._pull[id], newItem, false);
				if (this.config.update) {
					this.config.update(this._pull[id]);
				}
				if (!silent) {
					this._onChange("update", id, this._pull[id]);
				}
			}
			this._applySmart();
		} else {
			dhxWarning("item not found");
		}
	}
	getIndex(id: Id): number {
		if (!id) {
			return -1;
		}
		const res = findIndex(this._order, item => item && item.id.toString() === id.toString());
		if (this._pull[id] && res >= 0) {
			return res;
		}
	}
	getId(index: number): Id {
		if (!this._order[index]) {
			return;
		}
		return this._order[index].id;
	}
	getLength() {
		return this._order.length;
	}
	isDataLoaded(from = 0, to: number = this._order.length): boolean {
		if (isNumeric(from) && isNumeric(to)) {
			return this._order.slice(from, to).filter(item => item && item.$empty).length === 0;
		}

		// if check succeeds once, collection can't go back to not-loaded state
		if (!this._loaded) {
			this._loaded = !this.find(item => item.$empty);
		}

		return !!this._loaded;
	}
	filter(rule?: IFilterMode | IFilterCallback, config?: IFilterConfig) {
		if (!this.isDataLoaded()) {
			dhxWarning("the method doesn't work with lazyLoad");
			return;
		}

		if (!config || !config.add) {
			this._order = this._initOrder || this._order;
			this._initOrder = null;
		}

		if (rule && typeof rule !== "function") {
			const t = rule as IFilterMode;
			if (t.by !== undefined && t.match !== undefined) {
				rule = t.compare
					? obj => t.compare(obj[t.by], t.match, obj, t.multi)
					: obj => obj[t.by] == t.match;
			}
		}
		this._filter = config && config.smartFilter ? rule : null;

		this._applyFilters(rule as IFilterCallback);
		this.events.fire(DataEvents.change);
	}
	find(conf: IFilterMode | DataCallback<T>): any {
		for (const key in this._pull) {
			const res = findByConf(this._pull[key], conf);
			if (res) {
				return res;
			}
		}
		return null;
	}
	findAll(conf: IFilterMode | DataCallback<T>): any[] {
		const res = [];
		for (const key in this._pull) {
			const item = findByConf(this._pull[key], conf);
			if (item) {
				res.push(item);
			}
		}
		return res;
	}
	sort(rule?: ISortMode, config?: ISortConfig): void {
		if (!this.isDataLoaded()) {
			dhxWarning("the method doesn't work with lazyLoad");
			return;
		}

		if (config && config.smartSorting) {
			this._sorter = rule;
		}

		if (rule) {
			this._applySorters(rule);
		}

		this.events.fire(DataEvents.change);
	}
	copy(id: Id | Id[], index: number, target?: IDataCollection | ITreeCollection, targetId?: Id): Id | Id[] {
		if (id instanceof Array) {
			return id.map((elementId, key) => {
				return this._copy(elementId, index, target, targetId, key);
			});
		} else {
			return this._copy(id, index, target, targetId);
		}
	}
	move(
		id: Id | Id[],
		index: number,
		target?: DataCollection | TreeCollection,
		targetId?: Id,
		newId?: Id
	): Id | Id[] {
		if (id instanceof Array) {
			return id.map((elementId, key) => {
				return this._move(elementId, index, target, targetId, key);
			});
		} else {
			return this._move(id, index, target, targetId, 0, newId);
		}
	}
	forEach(callback: DataCallback<T>): void {
		for (let i = 0; i < this._order.length; i++) {
			callback.call(this, this._order[i], i, this._order);
		}
	}
	load(url: IDataProxy | string, driver?: IDataDriver | DataDriver): Promise<any> {
		if (typeof url === "string") {
			this.dataProxy = url = new DataProxy(url);
		}
		this.dataProxy = url;
		return this._loader.load(url, driver);
	}
	parse(data: T[], driver?: DataDriver | IDataDriver) {
		this._reset();
		return this._loader.parse(data, driver);
	}
	$parse(data: any[]) {
		const apx = this.config.approximate;
		if (apx) {
			data = this._approximate(data, apx.value, apx.maxNum);
		}

		this._parse_data(data);
		this._applySmart();

		this.events.fire(DataEvents.change, ["load"]);
		this.events.fire(DataEvents.load);
	}
	save(url: IDataProxy | string) {
		if (typeof url === "string") {
			url = new DataProxy(url);
		}
		this._loader.save(url);
	}
	changeId(id: Id, newId: Id = uid(), silent?: boolean) {
		if (!silent && !this.isDataLoaded()) {
			dhxWarning("the method doesn't work with lazyLoad");
			return;
		}
		const item = this.getItem(id);
		if (!item) {
			dhxWarning("item not found");
		} else {
			item.id = newId;
			extend(this._pull[id], item);
			this._pull[newId] = this._pull[id];
			if (!silent) {
				this._onChange("update", newId, this._pull[newId]);
			}
			delete this._pull[id];
		}
	}
	// todo: loop through the array and check saved statuses
	isSaved(): boolean {
		return !this._changes.order.length; // todo: bad solution, errors and holded elments are missed...
	}
	map(callback: DataCallback<T>): any[] {
		const result: any[] = [];
		for (let i = 0; i < this._order.length; i++) {
			result.push(callback.call(this, this._order[i], i, this._order));
		}
		return result;
	}
	mapRange(from: number, to: number, callback: DataCallback<T>): any[] {
		if (from < 0) {
			from = 0;
		}
		if (to > this._order.length - 1) {
			to = this._order.length - 1;
		}
		const arr = this._order.slice(from, to);
		const result: any[] = [];
		for (let i = from; i <= to; i++) {
			result.push(callback.call(this, this._order[i], i, arr));
		}
		return result;
	}
	reduce<A>(callback: ReduceCallBack<T, A>, acc: A) {
		for (let i = 0; i < this._order.length; i++) {
			acc = callback.call(this, acc, this._order[i], i);
		}
		return acc;
	}
	serialize(driver: DataDriver = DataDriver.json) {
		// remove $ attrs
		const data = [];
		for (let index = 0; index < this._order.length; index++) {
			const item = { ...this._order[index] };
			Object.keys(item).forEach(key => {
				if (key.startsWith("$")) {
					delete item[key];
				}
			});
			data.push(item);
		}
		const dataDriver = toDataDriver(driver);
		if (dataDriver) {
			return dataDriver.serialize(data);
		}
	}
	getInitialData() {
		return this._initOrder;
	}
	setMeta(obj: T, key: string, value: any) {
		if (!obj) return;
		let map = this._meta.get(obj);
		if (!map) {
			map = {};
			this._meta.set(obj, map);
		}
		map[key] = value;
	}
	getMeta(obj: T, key: string): any {
		const map = this._meta.get(obj);
		return map ? map[key] : null;
	}
	getMetaMap(obj: T): any {
		return this._meta.get(obj);
	}
	setRange(from: number, to: number): void {
		this._range = !to ? null : [from, to];
	}
	getRawData(from: number, to: number, order?: T[], mode?: number): T[] {
		order = order || this._order;
		if (mode === 1) return order;

		if (this._range) {
			from = this._range[0] + from;
			if (to === -1) {
				to = this._range[1];
			} else {
				const diff = Math.abs(to - from);
				to = from + diff > this._range[1] ? this._range[1] : from + diff;
			}
		}

		if (!to || (from === 0 && (to === -1 || to === order.length))) {
			return order;
		}

		if (from >= order.length) return [];
		if (to === -1 || to > order.length) to = order.length;

		const slice = order.slice(from, to);

		if (slice.filter(item => item.$empty).length !== 0) {
			this.events.fire(DataEvents.dataRequest, [from, to]);
		}

		return slice;
	}
	protected _add(newItem: IDataItem, index: number): Id {
		if (!this.isDataLoaded()) {
			dhxWarning("the method doesn't work with lazyLoad");
			return;
		}
		const id = this._addCore(newItem, index);
		this._onChange("add", newItem.id, newItem);
		this.events.fire(DataEvents.afterAdd, [newItem]);
		return id;
	}
	protected _remove(id: Id) {
		if (!this.isDataLoaded()) {
			dhxWarning("the method doesn't work with lazyLoad");
			return;
		}
		const removedItem = this._pull[id];
		if (removedItem) {
			if (!this.events.fire(DataEvents.beforeRemove, [removedItem])) {
				return;
			}
			this._removeCore(removedItem.id);
			this._onChange("remove", id, removedItem);
		}
		this.events.fire(DataEvents.afterRemove, [removedItem]);
	}
	protected _copy(
		id: Id,
		index: number,
		target?: IDataCollection | ITreeCollection,
		targetId?: Id,
		key?: number
	): Id {
		if (!this.isDataLoaded()) {
			dhxWarning("the method doesn't work with lazyLoad");
			return;
		}
		if (!this.exists(id)) {
			return null;
		}
		const newid = uid();
		if (key) {
			index = index === -1 ? -1 : index + key;
		}
		if (target) {
			if (!(target instanceof DataCollection) && targetId) {
				target.add(copyWithoutInner(this.getItem(id)), index);
				return;
			}
			if (target.exists(id)) {
				target.add({ ...copyWithoutInner(this.getItem(id)), id: newid }, index);
				return newid;
			} else {
				target.add(copyWithoutInner(this.getItem(id)), index);
				return id;
			}
		}
		this.add({ ...copyWithoutInner(this.getItem(id)), id: newid }, index);
		return newid;
	}
	protected _move(
		id: Id,
		index: number,
		target?: IDataCollection | ITreeCollection,
		targetId?: Id,
		key?: number,
		newId?: Id
	): Id {
		if (!this.isDataLoaded()) {
			dhxWarning("the method doesn't work with lazyLoad");
			return;
		}
		if (key) {
			index = index === -1 ? -1 : index + key;
		}
		if (target && target !== this && this.exists(id)) {
			const item = copy(this.getItem(id), true);
			if (newId) item.id = newId;
			if ((!newId && target.exists(id)) || target.exists(newId)) {
				item.id = uid();
			}
			if (targetId) {
				item.parent = targetId;
			}
			target.add(item, index);
			// remove data from original collection
			this.remove(id);
			return item.id;
		}
		if (this.getIndex(id) === index) {
			return null;
		}
		// move other elements
		const spliced = this._order.splice(this.getIndex(id), 1)[0];
		if (index === -1) {
			index = this._order.length;
		}
		this._order.splice(index, 0, spliced);

		this.events.fire(DataEvents.change, [id, "update", this.getItem(id)]);
		return id;
	}
	protected _addCore(obj: IDataItem, index: number): Id {
		if (this.config.init) {
			obj = this.config.init(obj);
		}

		obj.id = obj.id ?? uid();

		if (this._pull[obj.id]) {
			dhxError(`Item ${obj.id} already exist`);
		}
		// todo: not ideal solution
		if (this._initOrder && !isTreeCollection(this)) {
			this._addToOrder(this._initOrder, obj, index);
		}
		this._addToOrder(this._order, obj, index);

		return obj.id;
	}
	protected _removeCore(id: Id) {
		if (this.getIndex(id) >= 0) {
			this._order = this._order.filter(el => el.id !== id);
			delete this._pull[id];
		}

		if (this._initOrder && this._initOrder.length) {
			this._initOrder = this._initOrder.filter(el => el.id !== id);
			delete this._pull[id];
		}
	}
	protected _parse_data(data: any[]) {
		let index = this._order.length;
		if (this.config.prep) {
			data = this.config.prep(data);
		}
		for (const obj of data) {
			this._addCore(obj, index++);
		}
	}
	protected _approximate(data: any[], values: string[], maxNum: number) {
		const len = data.length;
		const vlen = values.length;
		const rlen = Math.floor(len / maxNum);
		const newData = Array(Math.ceil(len / rlen));

		let index = 0;
		for (let i = 0; i < len; i += rlen) {
			const newItem = copy(data[i]);
			const end = Math.min(len, i + rlen);
			for (let j = 0; j < vlen; j++) {
				let sum = 0;
				for (let z = i; z < end; z++) {
					sum += data[z][values[j]];
				}
				newItem[values[j]] = sum / (end - i);
			}
			newData[index++] = newItem;
		}

		return newData;
	}
	protected _onChange(status: Statuses, id: Id, obj: any): void {
		let itemCount = 0;
		const maxStack = 10;
		for (let item of this._changes.order) {
			// update pending item if previous state is "saving" or if item not saved yet
			const index = this._changes.order.indexOf(item);
			if (item.id === id && !item.saving) {
				itemCount += 1;
				if (index === this._changes.order.length - 1 || this._changes.order[index + 1].id !== id) {
					// update item
					if (item.error) {
						item.error = false;
					}
					item = { ...item, obj, status };
					itemCount += 1;
					if (itemCount > maxStack) {
						this._changes.order.splice(index, itemCount - maxStack, item);
					} else {
						this._changes.order.splice(index + 1, 0, item);
					}
					this._loader.updateChanges(this._changes);

					if (status === "remove" && obj.$emptyRow) return;
					this.events.fire(DataEvents.change, [id, status, obj]);
					return;
				}
			}
		}
		this._changes.order.push({ id, status, obj: { ...obj }, saving: false });
		this._loader.updateChanges(this._changes);
		this.events.fire(DataEvents.change, [id, status, obj]);
	}
	protected _addToOrder(array: any[], obj: any, index?: number) {
		if (index >= 0 && array[index]) {
			this._pull[obj.id] = obj;
			array.splice(index, 0, obj);
		} else {
			this._pull[obj.id] = obj;
			array.push(obj);
		}
	}

	protected _applySmart() {
		if (this._filter) {
			this._applyFilters();
		}

		if (this._sorter) {
			this._applySorters();
		}
	}

	protected _applySorters(by?: ISortMode) {
		this._sort.sort(this._order, by, this._sorter);
		// sort the not-filtered dataset
		if (this._initOrder && this._initOrder.length) {
			this._sort.sort(this._initOrder, by, this._sorter);
		}
	}
	protected _applyFilters(rule?: IFilterCallback) {
		const filter = this._filter;
		if (rule === filter) rule = null;

		if (rule || filter) {
			const fOrder = this._order.filter(
				item => (rule ? rule(item) : true) && (filter ? filter(item) : true)
			);
			if (!this._initOrder) {
				this._initOrder = this._order;
			}
			this._order = fOrder;
		}
	}
}
